/**
 * recordDataNormalizer - 病历数据归一化工具
 *
 * 确保从 UI → API → 数据库，所有病历字段都以字符串形式传递和存储。
 * 确保患者信息字段的一致性和正确处理。
 *
 * @module clinical-record/shared/utils/recordDataNormalizer
 */

import { RECORD_PATIENT_FIELDS } from "./patientInfoNormalizer";

export { normalizeAge, normalizeGender } from "./patientInfoNormalizer";

/** 支持字符串类型的病历字段 */
export const STRING_FIELDS = new Set([
  "chief_complaint",
  "present_illness",
  "past_history",
  "allergy_history",
  "family_history",
  "personal_history",
  "physical_examination_text",
  "lab_results_text",
  "imaging_results_text",
  "other_exams_text",
  "western_diagnosis_text",
  "western_treatment_plan",
  "tcm_symptoms",
  "tcm_diagnosis",
  "tcm_pattern_text",
  "tcm_treatment_plan",
  "tcm_prescription_text",
  "management_plan",
  "precautions",
  "tcm_health",
  "follow_up_plan",
  "doctor_advice",
]);

/** 需要特殊处理的字段（可能有别名或历史兼容性） */
export const ALIAS_MAPPINGS: Record<string, string> = {
  western_diagnosis: "western_diagnosis_text",
  western_treatment: "western_treatment_plan",
  tcm_pattern: "tcm_pattern_text",
  tcm_differentiation: "tcm_pattern_text",
  treatment_principle: "tcm_treatment_plan",
  prescription: "tcm_prescription_text",
  physical_examination: "physical_examination_text",
  follow_up_plan: "management_plan",
  lab_results: "lab_results_text",
  imaging_results: "imaging_results_text",
  other_exams: "other_exams_text",
};

/**
 * 清洗病历文本字段：去掉行尾空白、折叠空行、去除整体首尾空白。
 *
 * 病历字段是连续病历叙述（含编号分点），不是带段落间距的文档，
 * 因此空行一律折叠为普通换行，而不是保留一个空行。
 * 用于修正 AI 生成内容（如编号列表项间的空行）与历史数据里的大量留白。
 * 幂等：多次执行结果一致。
 */
export function sanitizeMedicalText(value: string): string {
  if (typeof value !== "string" || value.length === 0) return value;
  return value
    .replace(/\r\n/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{2,}/g, "\n")
    .replace(/^\s+|\s+$/g, "");
}

/**
 * 将任意值转换为字符串
 */
export function toString(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value === "string") {
    return value.trim();
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value
      .map((v) => toString(v))
      .filter(Boolean)
      .join("；");
  }

  if (typeof value === "object") {
    // 尝试从对象中提取文本
    const obj = value as Record<string, unknown>;
    for (const key of [
      "text",
      "content",
      "name",
      "value",
      "result",
      "description",
      "findings",
      "label",
    ]) {
      const candidate = obj[key];
      if (candidate) {
        return toString(candidate);
      }
    }
    // 如果没有找到合适的字段，将对象转换为 JSON
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }

  return String(value);
}

/**
 * 归一化病历数据，确保所有字段为字符串类型
 *
 * @param formData - 原始表单数据
 * @returns 归一化后的数据
 */
export function normalizeRecordData(
  formData: Record<string, unknown>,
): Record<string, unknown> {
  const normalized: Record<string, unknown> = {};

  // 处理所有字段
  for (const [key, value] of Object.entries(formData)) {
    // 跳过患者信息冗余字段（这些字段只读，不应提交）
    if (RECORD_PATIENT_FIELDS.has(key)) {
      continue;
    }

    // 跳过元数据字段
    if (key.startsWith("_")) {
      continue;
    }

    // 检查是否是字符串字段
    if (STRING_FIELDS.has(key)) {
      normalized[key] = toString(value);
      continue;
    }

    // 检查是否有别名映射
    if (ALIAS_MAPPINGS[key]) {
      const targetKey = ALIAS_MAPPINGS[key];
      normalized[targetKey] = toString(value);
      continue;
    }

    // 其他字段保持原样（如 tags 等特殊字段）
    normalized[key] = value;
  }

  return normalized;
}

/**
 * 归一化单个字段值
 */
export function normalizeFieldValue(
  fieldName: string,
  value: unknown,
): unknown {
  if (STRING_FIELDS.has(fieldName) || ALIAS_MAPPINGS[fieldName]) {
    return toString(value);
  }
  return value;
}

/**
 * 清理空值
 * - 字符串字段：允许空字符串（用于清空字段）
 * - 非字符串字段：过滤掉空值
 */
export function cleanEmptyValues(
  data: Record<string, unknown>,
): Record<string, unknown> {
  const cleaned: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(data)) {
    if (value === undefined || value === null) {
      continue;
    }

    if (typeof value === "string" && STRING_FIELDS.has(key)) {
      // 字符串字段允许空字符串
      cleaned[key] = value;
    } else if (value !== "" && value !== undefined && value !== null) {
      cleaned[key] = value;
    }
  }

  return cleaned;
}

/**
 * 完整的归一化流程：归一化 + 清理
 */
export function normalizeAndCleanRecordData(
  formData: Record<string, unknown>,
): Record<string, unknown> {
  const normalized = normalizeRecordData(formData);
  return cleanEmptyValues(normalized);
}
